import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { Row, Col, Button } from 'react-bootstrap';
import {
  Formik,
  Form as FormikForm,
  FieldArray,
  useFormikContext,
} from 'formik';
import deleteIcon from 'images/icons/invite/delete.svg';
import usePolicyTemplates from 'components/Common/PolicyTemplates/Hooks/usePolicyTemplates';
import usePaymentMethods from 'components/Common/Billing/Hooks/usePaymentMethods';
import renewalStatuses from 'data/renewal_method_statuses';
import {
  TextField,
  TextFieldWithAddon,
  TextAreaField,
  RadioButtonsField,
  CheckBoxField,
  DateField,
  SelectFieldV2,
  SwitchField,
  SubmitButton,
  CancelPopupButton,
} from '../../ui/FormElement';

import PolicyPlanCustomRecordTemplateContext from '../../contexts/PolicyPlanCustomRecordTemplateContext';

import NumberEmployeesFiels from './Fields/NumberEmployeesFiels';
import MonthlyDiscount from './Fields/MonthlyDiscount';
import MonthlyDiscountName from './Fields/MonthlyDiscountName';
import OnetimeDiscount from './Fields/OnetimeDiscount';
import MonthlyTotal from './Fields/MonthlyTotal';
import OneTimeTotal from './Fields/OneTimeTotal';
import OnetimeDiscountName from './Fields/OnetimeDiscountName';
import ChooseRecordModalForm from './ChooseCustomRecord/Modal';
import SessionsCovered from './Fields/SessionsCovered';
import AnnualDiscount from './Fields/AnnualDiscount';
import ActivationFee from './Fields/ActivationFee';
import DescriptionGroups from './Fields/DescriptionGroups';
import CurrencyPrice from './Fields/CurrencyPrice';
import IsFree from './Fields/IsFree';
import IsPayPerUsed from './Fields/IsPayPerUsed';
import statuses from '../../data/policy_plan_statuses';

import sessions from '../../data/sessions';

const statusScope = (currentStatus) => {
  if (currentStatus == undefined || currentStatus == '') {
    return statuses;
  }
  if (currentStatus == 'draft') {
    return statuses.filter((item) => ['draft'].includes(item.value));
  }
  if (currentStatus == 'active') {
    return statuses.filter((item) => item.value != 'draft' && item.value != 'expired');
  }
  return statuses.filter((item) => currentStatus == item.value);
};

export default function Form({
  onSubmit,
  onCancel,
  template,
  typeView = 'template',
}) {
  const { loading: policyTemplateLoading, templates: policyTemplates } = typeView === 'template' ? usePolicyTemplates() : {};

  const [loader, setLoader] = useState(false);
  const [showChooseRecordOneTime, setShowChooseRecordOneTime] = useState(false);
  const [showChooseRecordMonth, setShowChooseRecordMonth] = useState(false);

  const [prevStatus, setPrevStatus] = useState('');

  const paymentMethods = typeView === 'policy' ? usePaymentMethods(template.enterprise_id) : [];

  const validationSchema = {
    name: Yup.string().required(`${typeView === 'policy' ? 'Name' : 'Default Name'} is required`),
    sessions: Yup.number().required('required'),
    monthly_discount: Yup.number()
      .typeError('must be a number')
      .when('monthly_discount_enable', {
        is: true,
        then: (schema) => schema.required('required'),
      }),
    monthly_discount_name: Yup.string().when('monthly_discount_enable', {
      is: true,
      then: (schema) => schema.required('required'),
    }),
    onetime_discount: Yup.number()
      .typeError('must be a number')
      .when('onetime_discount_enable', {
        is: true,
        then: (schema) => schema.required('required'),
      }),
    onetime_discount_name: Yup.string().when('onetime_discount_enable', {
      is: true,
      then: (schema) => schema.required('required'),
    }),
    sessions_covered: Yup.number()
      .typeError('must be a number')
      .when('sessions_covered_enable', {
        is: true,
        then: (schema) => schema.required('required'),
      }),
    currency_prices: Yup.array().of(
      Yup.object().shape({
        currency: Yup.string().required('required'),
        price: Yup.number().required('required'),
      }),
    ),
  };

  if (typeView == 'policy') {
    validationSchema.effective_date = Yup.string()
      .required('required')
      .min(5, 'required');

    validationSchema.employees_covered = Yup.number()
      .typeError('must be a number')
      .when('cover_everybody', {
        is: false,
        then: (schema) => schema.required('required'),
      });

    validationSchema.price_employee_month = Yup.number()
      .typeError('must be a number')
      .when('is_free && !is_pay_per_used', {
        is: false,
        then: (schema) => schema.required('required'),
      });

    validationSchema.price_session = Yup.number()
      .typeError('must be a number')
      .when('is_free && is_pay_per_used', {
        is: false,
        then: (schema) => schema.required('required'),
      });

    validationSchema.onetime_custom_records = Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('required'),
        price: Yup.string().required('required'),
      }),
    );

    validationSchema.monthly_custom_records = Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('required'),
        price: Yup.string().required('required'),
      }),
    );

    validationSchema.payment_information_id = Yup.string()
      .when('is_free', {
        is: false,
        then: (schema) => schema.required('Payment Method is required'),
      });
  }
  if (typeView == 'template') {
    validationSchema.policy_template_id = Yup.number()
      .typeError('Choose Policy Category');

    validationSchema.onetime_custom_records = Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('required'),
        currency_prices: Yup.array().of(
          Yup.object().shape({
            currency: Yup.string().required('required'),
            price: Yup.number().required('required'),
          }),
        ),
      }),
    );

    validationSchema.monthly_custom_records = Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('required'),
        currency_prices: Yup.array().of(
          Yup.object().shape({
            currency: Yup.string().required('required'),
            price: Yup.number().required('required'),
          }),
        ),
      }),
    );
  }

  const FormSchema = Yup.object().shape(validationSchema);
  const [formData, setFormData] = useState(
    template == undefined
      ? {
        name: '',
        description: '',
        policy_template_id: null,
        sessions: '6',
        billing_duration_month: 12,
        cover_everybody: true,
        employees_covered: '',
        price_employee_month: 0,
        price_session: 0,
        monthly_discount_enable: false,
        monthly_discount: '',
        onetime_discount_enable: false,
        onetime_discount: '',
        monthly_custom_records: [],
        onetime_custom_records: [],
        effective_date: '',
        payment_mode: false,
        sessions_covered_enable: false,
        sessions_covered: '',
        monthly_discount_name: 'Network Discount',
        onetime_discount_name: 'Network Discount',
        annual_discount: '',
        renewal_date: '',
        description_left_column: [],
        description_right_column: [],
        category_default: false,
        renewal_method: 'automatic',
        currency_prices: [{
          currency: 'USD',
          price: '',
        }, {
          currency: 'CAD',
          price: '',
        }, {
          currency: 'GBP',
          price: '',
        }, {
          currency: 'CHF',
          price: '',
        }],
        session_currency_prices: [{
          currency: 'USD',
          price: '',
        }, {
          currency: 'CAD',
          price: '',
        }, {
          currency: 'GBP',
          price: '',
        }, {
          currency: 'CHF',
          price: '',
        }],
        is_free: false,
        is_pay_per_used: false,
      }
      : {
        ...template,
        effective_date: template.effective_date_form,
        renewal_date: template.renewal_date_form,
        sessions: template.sessions.toString(),
        payment_mode:
            template.payment_mode && template.payment_mode == 'annual',
      },
  );

  const onSubmitHundler = (values, { setSubmitting, setErrors }) => {
    setSubmitting(false);
    setLoader(true);
    const processsValues = {
      ...values,
      payment_mode: values.payment_mode ? 'annual' : 'monthly',
      price_employee_month: values.is_free || values.is_pay_per_used ? 0 : values.price_employee_month,
      price_session: values.is_free || !values.is_pay_per_used ? 0 : values.price_session,
    };
    if (onSubmit != undefined) {
      onSubmit(processsValues, (errorsData) => {
        const newErrors = {};
        errorsData.errors.forEach((item) => {
          newErrors[item.source.pointer.replace('/data/attributes/', '')] = item.detail;
        });
        setErrors(newErrors);
        setLoader(false);
      });
    }
  };

  const isReadonly = typeView == 'policy' && template && template.status != 'draft';
  const numberEmployeesEnable = typeView == 'policy'
    && template
    && template.status == 'active'
    && template.cover_everybody
    && template.payment_mode == 'monthly';

  const isReadonlyPaymentMethod = typeView == 'policy' && template && template.status != 'draft' && !template.active;

  return (
    <PolicyPlanCustomRecordTemplateContext>
      <Formik
        enableReinitialize
        validationSchema={FormSchema}
        initialValues={formData}
        onSubmit={onSubmitHundler}
      >
        {({ values, setFieldValue }) => {
          useEffect(() => {
            if ((prevStatus === 'active' || prevStatus === 'trial_cancelled') && values.status === 'cancelled') {
              const currentDate = new Date();
              const options = {
                month: 'long',
                day: '2-digit',
                year: 'numeric',
              };
              setFieldValue('renewal_date', currentDate.toLocaleDateString('en-US', options));
              $('.effective-date-wrapper .input-datepicker').datepicker(
                'setDate',
                currentDate,
              );
            }
            setPrevStatus(values.status);
          }, [values.status, setFieldValue]);

          return (
            <FormikForm className="needs-validation" noValidate="">
              <div className="ibox-content mt20">
                <Row>
                  <Col md={typeView == 'template' ? 12 : 8}>
                    <TextField
                      name="name"
                      label={(
                        <>
                          <span>{typeView === 'policy' ? 'Name' : 'Default Name'}</span>
                          <abbr title="required">*</abbr>
                        </>
                    )}
                    />

                    <TextAreaField
                      name="description"
                      label={(
                        <span>Description</span>
                    )}
                    />

                    <Row>
                      <Col md={6}>
                        <h3>Left Description</h3>
                        <FieldArray name="description_left_column">
                          {(helpers) => (
                            <DescriptionGroups
                              parentName="description_left_column"
                              helpers={helpers}
                              collection={values.description_left_column}
                              isReadonly={isReadonly || typeView === 'policy'}
                            />
                          )}
                        </FieldArray>
                      </Col>
                      <Col md={6}>
                        <h3>Right Description</h3>
                        <FieldArray name="description_right_column">
                          {(helpers) => (
                            <DescriptionGroups
                              parentName="description_right_column"
                              helpers={helpers}
                              collection={values.description_right_column}
                              isReadonly={isReadonly || typeView === 'policy'}
                            />
                          )}
                        </FieldArray>
                      </Col>
                    </Row>

                    {typeView == 'policy' && (
                    <TextField
                      name="policy_template_name"
                      disabled
                      label={(
                        <>
                          <span>Category</span>
                          <abbr title="required">*</abbr>
                        </>
                      )}
                    />
                    )}

                    {typeView === 'policy' && !values.is_free && (
                    <SelectFieldV2
                      name="payment_information_id"
                      placeholder={paymentMethods === null ? 'Payment Method Loading ...' : 'Choose Payment Method'}
                      isMulti={false}
                      options={paymentMethods && paymentMethods.map((item) => ({
                        label: item.attributes.label,
                        value: item.id,
                      }))}
                      isDisabled={isReadonlyPaymentMethod}
                      label={(
                        <>
                          <span>Payment Method</span>
                          <abbr title="required">*</abbr>
                        </>
                  )}
                    />
                    )}

                    {typeView === 'template' && (
                    <Row>
                      <Col md={6}>
                        <SelectFieldV2
                          name="policy_template_id"
                          placeholder={policyTemplateLoading ? 'Policy Categories Loading ...' : 'Choose Policy Category'}
                          isMulti={false}
                          options={policyTemplates && policyTemplates.map((item) => ({
                            label: item.attributes.title,
                            value: item.id,
                          }))}
                          label={(
                            <>
                              <span>Policy Category</span>
                              <abbr title="required">*</abbr>
                            </>
                    )}
                        />
                      </Col>
                      <Col md={6}>
                        <div className="mt20">
                          <CheckBoxField
                            disabled={isReadonly}
                            name="category_default"
                            label="Category Default Flag"
                            inline
                          />
                        </div>
                      </Col>
                    </Row>
                    )}
                  </Col>
                  {typeView == 'policy' && (
                  <Col md={4}>
                    <DateField
                      name="effective_date"
                      disabled={isReadonly}
                      label={(
                        <>
                          <span>Effective Date</span>
                          <abbr title="required">*</abbr>
                        </>
                      )}
                    />
                    <SelectFieldV2
                      name="status"
                      placeholder="Choose Status"
                      isMulti={false}
                      options={statusScope(
                        template == undefined ? '' : template.status,
                      )}
                      // isDisabled={isReadonly}
                      label={(
                        <>
                          <span>Status</span>
                          <abbr title="required">*</abbr>
                        </>
                      )}
                    />
                    {template
                      && (
                        (template.status === 'active'
                            && values.status == 'cancelled') || (
                          template.status === 'cancelled'
                              || template.status === 'trial_cancelled'
                        )
                      ) && (
                        <div className="effective-date-wrapper">
                          <DateField
                            name="renewal_date"
                            disabled={template.status === 'cancelled'
                          || template.status === 'trial_cancelled'}
                            label={(
                              <>
                                <span>{values.status == 'cancelled' || values.status == 'trial_cancelled' ? 'Cancellation Date' : 'Renewal Date'}</span>
                                <abbr title="required">*</abbr>
                              </>
                          )}
                          />
                        </div>
                    )}
                  </Col>
                  )}
                </Row>
              </div>
              <div className="ibox-content mt20">
                <h2>Subscription Premium</h2>
                <Row>
                  <Col md={4}>
                    <label>Sessions</label>
                  </Col>
                  <Col md={6}>
                    <RadioButtonsField
                      disabled={isReadonly}
                      name="sessions"
                      options={sessions}
                    />
                  </Col>
                </Row>

                <Row className="mt20">
                  <Col md={4}>
                    <label>Number of Employees Covered</label>
                  </Col>
                  <Col md={4}>
                    <NumberEmployeesFiels
                      isDisabled={isReadonly && !numberEmployeesEnable}
                    />
                  </Col>
                  <Col md={4}>
                    <CheckBoxField
                      disabled={isReadonly}
                      name="cover_everybody"
                      label="Cover Everybody"
                      inline
                    />
                  </Col>
                </Row>
                {typeView == 'policy' && (
                <Row className="mt20">
                  <Col md={4}>
                    <label>Plan Billing Cycle</label>
                  </Col>
                  <Col md={4}>
                    <Row>
                      <Col md={4}>Monthly</Col>
                      <Col className="text-center" md={4}>
                        <SwitchField
                          name="payment_mode"
                          disabled={isReadonly || values.is_free}
                        />
                      </Col>
                      <Col className="text-right" md={4}>
                        Annual
                      </Col>
                    </Row>
                  </Col>
                </Row>
                )}

                {!values.is_free && (
                <Row className="mt10">
                  <Col md={4}>
                    <label>Billing Duration</label>
                  </Col>
                  <Col md={4}>
                    <TextField
                      name="billing_duration_month"
                      disabled={isReadonly}
                    />
                  </Col>
                  <Col md={4}>
                    Months
                  </Col>
                </Row>
                )}

                <Row className="mt10">
                  <Col md={4}>
                    <label>Renewal Method</label>
                  </Col>
                  <Col md={4}>
                    <SelectFieldV2
                      name="renewal_method"
                      placeholder="Choose Renewal Method"
                      isMulti={false}
                      options={renewalStatuses}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                      }}
                      menuPortalTarget={document.body}
                      isDisabled={isReadonly}
                    />
                  </Col>
                </Row>

                {typeView === 'policy' && (
                  <>
                    <Row className="mt10">
                      <Col md={4}>
                        <label>Free Plan</label>
                      </Col>
                      <Col md={4}>
                        <IsFree isDisabled={isReadonly} />
                      </Col>
                    </Row>
                    <Row className="mt10">
                      <Col md={4}>
                        <label>Pay As You Go</label>
                      </Col>
                      <Col md={4}>
                        <IsPayPerUsed isDisabled={isReadonly} />
                      </Col>
                    </Row>
                  </>
                )}
                {!values.is_free && values.is_pay_per_used && (
                <Row className="mt20">
                  <Col md={4}>
                    <label>Price Per Session</label>
                  </Col>
                  <Col md={4}>
                    <TextFieldWithAddon
                      name="price_session"
                      addon={template.currency.mark}
                      disabled={isReadonly}
                      type="number"
                      min="0"
                      step="0.01"
                      pattern="d+(.d{1,2})?"
                    />
                  </Col>
                </Row>
                )}
                {!values.is_free && !values.is_pay_per_used && (
                <Row className="mt20">
                  <Col md={4}>
                    <label>Price Per Employee'</label>
                  </Col>
                  <Col md={4}>
                    {typeView === 'policy'
                      ? (
                        <TextFieldWithAddon
                          name="price_employee_month"
                          addon={template.currency.mark}
                          disabled={isReadonly}
                          type="number"
                          min="0"
                          step="0.01"
                          pattern="d+(.d{1,2})?"
                        />
                      )
                      : <CurrencyPrice isReadonly={isReadonly} showDeleteButton={false} showAddButton={false} />}
                  </Col>
                  <Col md={4}>
                    <div className="mt5">Per Month</div>
                  </Col>
                </Row>
                )}

                {typeView === 'template'
                && (
                <Row className="mt20">
                  <Col md={4}>
                    <label>Price Per Session</label>
                  </Col>
                  <Col md={4}>

                    <CurrencyPrice
                      prefixName="session_"
                      isReadonly={isReadonly}
                      showDeleteButton={false}
                      showAddButton={false}
                    />
                  </Col>
                </Row>
                )}

                <FieldArray name="monthly_custom_records">
                  {({ insert, remove, push }) => (
                    <div>
                      <h3>Custom Subscription Premium</h3>
                      {values.monthly_custom_records.map(
                        (customRecord, index) => (
                          <Row>
                            <Col md={4}>
                              <TextField
                                name={`monthly_custom_records.${index}.name`}
                                placeholder="Set Name"
                                disabled={isReadonly}
                              />
                            </Col>
                            <Col md={4}>
                              {typeView === 'policy' ? (
                                <TextFieldWithAddon
                                  name={`monthly_custom_records.${index}.price`}
                                  type="number"
                                  addon={template.currency.mark}
                                  placeholder="Set Price"
                                  disabled={isReadonly}
                                  min="0"
                                  step="0.01"
                                  pattern="d+(.d{1,2})?"
                                />
                              ) : (
                                <div>
                                  {customRecord.currency_prices
                                  && (
                                  <CurrencyPrice
                                    isReadonly={isReadonly}
                                    collection={customRecord.currency_prices}
                                    prefixName={`monthly_custom_records.${index}.`}
                                    showDeleteButton={false}
                                    showAddButton={false}
                                  />
                                  )}
                                </div>
                              )}

                            </Col>
                            <Col md={4}>
                              <Row>
                                <Col md={4}>
                                  <div className="mt5">Per Month</div>
                                </Col>
                                {!isReadonly && (
                                <Col md={8}>
                                  <a onClick={() => remove(index)}>
                                    <img src={deleteIcon} />
                                  </a>
                                </Col>
                                )}
                              </Row>
                            </Col>
                          </Row>
                        ),
                      )}
                      {!isReadonly && (
                      <Row>
                        <Col md={4} mdOffset={4} className="text-right">
                          <Button
                            onClick={() => setShowChooseRecordMonth(true)}
                            bsStyle="primary"
                            disabled={values.is_free}
                          >
                            Add Service
                          </Button>

                          <ChooseRecordModalForm
                            typeView={typeView}
                            currency={typeView === 'policy' ? template.currency.title : 'USD'}
                            show={showChooseRecordMonth}
                            onHideModal={() => setShowChooseRecordMonth(false)}
                            onSave={(values) => {
                              push(values);
                            }}
                          />
                        </Col>
                      </Row>
                      )}
                    </div>
                  )}
                </FieldArray>

                <h3>Additional Options</h3>
                <Row className="mt10">
                  <Col md={4}>
                    <label>Annual Billing Discount</label>
                  </Col>
                  <Col md={4}>
                    <AnnualDiscount typeView={typeView} isDisabled={isReadonly} />
                  </Col>
                </Row>
                <Row className="mt10">
                  <Col md={4}>
                    <MonthlyDiscountName />
                  </Col>
                  <Col md={4}>
                    <MonthlyDiscount isDisabled={isReadonly || values.is_free} />
                  </Col>
                  <Col md={4}>
                    <CheckBoxField
                      name="monthly_discount_enable"
                      inline
                      disabled={isReadonly || values.is_free}
                    />
                  </Col>
                </Row>
                <Row className="mt10">
                  <Col md={4}>
                    <label>Sessions Covered By Employer</label>
                  </Col>
                  <Col md={4}>
                    <SessionsCovered isDisabled={isReadonly} />
                  </Col>
                  <Col md={4}>
                    <CheckBoxField
                      name="sessions_covered_enable"
                      inline
                      disabled={isReadonly}
                    />
                  </Col>
                </Row>
                <div className="text-right">
                  <MonthlyTotal typeView={typeView} template={template} />
                </div>
              </div>
              <div className="ibox-content mt20">
                <h2>One-Time Premium</h2>
                {typeView === 'policy' && <ActivationFee isReadonly={isReadonly} mark={template.currency.mark} />}
                <FieldArray name="onetime_custom_records">
                  {({ remove, push }) => (
                    <div>
                      <h3>Custom Services</h3>
                      {values.onetime_custom_records.map(
                        (customRecord, index) => (
                          <Row className={customRecord.is_activation_fee ? 'not-visible' : ''}>
                            <Col md={4}>
                              <TextField
                                name={`onetime_custom_records.${index}.name`}
                                placeholder="Set Name"
                                disabled={isReadonly}
                              />
                            </Col>
                            <Col md={4}>
                              {typeView === 'policy' ? (
                                <TextFieldWithAddon
                                  name={`onetime_custom_records.${index}.price`}
                                  type="number"
                                  addon={template.currency.mark}
                                  placeholder="Set Price"
                                  disabled={isReadonly}
                                  min="0"
                                  step="0.01"
                                  pattern="d+(.d{1,2})?"
                                />
                              ) : (
                                <div>
                                  {customRecord.currency_prices
                                  && (
                                  <CurrencyPrice
                                    isReadonly={isReadonly}
                                    collection={customRecord.currency_prices}
                                    prefixName={`onetime_custom_records.${index}.`}
                                    showDeleteButton={false}
                                    showAddButton={false}
                                  />
                                  )}
                                </div>
                              )}
                            </Col>
                            {!isReadonly && (
                            <Col md={4}>
                              <a onClick={() => remove(index)}>
                                <img src={deleteIcon} />
                              </a>
                            </Col>
                            )}
                          </Row>
                        ),
                      )}
                      {!isReadonly && (
                      <Row>
                        <Col md={4} mdOffset={4} className="text-right">
                          <Button
                            onClick={() => setShowChooseRecordOneTime(true)}
                            bsStyle="primary"
                            disabled={values.is_free}
                          >
                            Add Service
                          </Button>

                          <ChooseRecordModalForm
                            typeView={typeView}
                            currency={typeView === 'policy' ? template.currency.title : 'USD'}
                            show={showChooseRecordOneTime}
                            onHideModal={() => setShowChooseRecordOneTime(false)}
                            onSave={(newCustomRecord) => {
                              push({ ...newCustomRecord, is_activation_fee: false });
                            }}
                          />
                        </Col>
                      </Row>
                      )}
                    </div>
                  )}
                </FieldArray>
                <h3>Additional Options</h3>
                <Row className="mt10">
                  <Col md={4}>
                    <OnetimeDiscountName isDisabled={isReadonly || values.is_free} />
                  </Col>
                  <Col md={4}>
                    <OnetimeDiscount isDisabled={isReadonly || values.is_free} />
                  </Col>
                  <Col md={4}>
                    <CheckBoxField
                      name="onetime_discount_enable"
                      inline
                      disabled={isReadonly || values.is_free}
                    />
                  </Col>
                </Row>
                <div className="text-right">
                  <OneTimeTotal typeView={typeView} template={template} />
                </div>
              </div>
              <div className="text-right mt20">
                <CancelPopupButton
                  className="btn btn-primary btn-outline mr5 ml5 width-120"
                  title="Cancel"
                  onClick={() => onCancel()}
                />
                <SubmitButton
                  title={loader ? 'Please, wait ...' : 'Save'}
                  className={`btn width-120 btn-primary submit-button mr5 ml5 ${
                    loader ? 'not-possible-click' : ''
                  }`}
                />
              </div>
            </FormikForm>
          );
        }}
      </Formik>
    </PolicyPlanCustomRecordTemplateContext>
  );
}
